import { createApp, markRaw } from 'vue'
import Toast, { PluginOptions } from 'vue-toastification'
import { Vue3Mq } from 'vue3-mq'

import { useMainStore } from '@/stores/main'
import { createPinia } from 'pinia'
import { createPersistedState } from 'pinia-plugin-persistedstate'

import { FontAwesomeIcon, FontAwesomeLayers } from '@fortawesome/vue-fontawesome'
import { createGtm } from '@gtm-support/vue-gtm'
import { Ajax } from '@newrelic/browser-agent/features/ajax'
import { GenericEvents } from '@newrelic/browser-agent/features/generic_events'
import { JSErrors } from '@newrelic/browser-agent/features/jserrors'
import { SessionReplay } from '@newrelic/browser-agent/features/session_replay'
import { SessionTrace } from '@newrelic/browser-agent/features/session_trace'
import { Agent } from '@newrelic/browser-agent/loaders/agent'

import { initIconLibrary } from '@/lib/appIcons'
import { createReportingStorage } from '@/lib/reportingStorage'
import { appErrorService, wasReportedByAppErrorService } from '@/services/AppErrorService'
import {
  getErrorMessage,
  getErrorStack,
  isNoise,
  type NormalizedError
} from '@/services/errorNoiseFilters'

import '@VelocitySquared/cady-design-system/cady-storefront.css'
import './assets/scss/main.scss'
import 'vue-toastification/dist/index.css'
import App from './App.vue'
import router from './router'

new Agent({
  loader_config: {
    accountID: import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID,
    trustKey: import.meta.env.VITE_NEW_RELIC_TRUST_KEY,
    agentID: import.meta.env.VITE_NEW_RELIC_AGENT_ID,
    licenseKey: import.meta.env.VITE_NEW_RELIC_LICENSE_KEY,
    applicationID: import.meta.env.VITE_NEW_RELIC_APPLICATION_ID
  },
  info: {
    beacon: 'bam.nr-data.net',
    errorBeacon: 'bam.nr-data.net',
    licenseKey: import.meta.env.VITE_NEW_RELIC_LICENSE_KEY,
    applicationID: import.meta.env.VITE_NEW_RELIC_APPLICATION_ID,
    sa: 1
  },
  init: {
    ajax: {
      deny_list: [
        'bam.nr-data.net',
        'www.google-analytics.com',
        'api-v5.fulcrumsaas.net',
        'clarity.ms'
      ]
    },
    distributed_tracing: {
      enabled: true
    },
    session_replay: {
      enabled: true,
      block_selector: '',
      mask_text_selector: '*',
      sampling_rate: 0.01,
      error_sampling_rate: 25.0,
      mask_all_inputs: true,
      collect_fonts: true,
      inline_images: false,
      fix_stylesheets: true,
      preload: false,
      mask_input_options: {}
    }
  },
  features: [JSErrors, Ajax, SessionTrace, SessionReplay, GenericEvents]
})

// Stamp the build's version on every payload the agent sends, as
// `application.version` — the attribute the "Occurrences by version" panel on a
// New Relic error reads, and what makes a reported error attributable to a
// release. Set before `setErrorHandler` so an error thrown during startup is
// already decorated. `__APP_VERSION__` is the compile-time define fed by
// `VITE_APP_VERSION`, which both deployment workflows set from the release tag;
// a local or unstamped build reports `dev`.
window.newrelic?.setApplicationVersion(__APP_VERSION__)

const reportErrorsToNewRelic = import.meta.env.VITE_REPORT_ERRORS_TO_NEW_RELIC === 'true'

// The Browser Agent runs this filter over every error it is about to store,
// whether it auto-captured it or we reported it via `appErrorService`.
// Returning true tells the agent to drop the error entirely.
//
// The agent auto-captures exactly two things — `unhandledrejection` and window
// `error` events — and the listeners below cover both, so each throw reaches
// New Relic twice: once bare from the agent, once decorated by
// `AppErrorService` with the market and session token. That is what split a
// single rejection into a matched `UncaughtError` / `UnhandledPromiseRejection`
// pair. Drop the agent's copy and keep ours.
//
// Only when we are actually reporting, though: with
// `VITE_REPORT_ERRORS_TO_NEW_RELIC` off there is no copy of ours to keep, and
// dropping the agent's would silence the app completely.
window.newrelic?.setErrorHandler((err: unknown) => {
  if (reportErrorsToNewRelic && !wasReportedByAppErrorService(err)) return true

  // Past that point `err` is one of ours, already screened by the registry —
  // except from `reportError` / `reportVueRouterError`, which report without
  // consulting it. Screen here as a backstop, treating the error as a promise
  // rejection since that's the path NR's auto-capture bypass actually covers.
  //
  // Pass the agent's own object through untouched: re-wrapping it in a fresh
  // `Error` here threw away the origin signal the filters need, since for a
  // throw the agent could not treat as an `Error` it supplies an
  // `UncaughtError` carrying Safari's `sourceURL` / `line` / `column` in place
  // of a stack.
  const normalized: NormalizedError = {
    // Strip NR's "Unhandled Promise Rejection: " prefix so registry filters
    // match on the raw error message (e.g. `Load failed`).
    message: getErrorMessage(err).replace(/^Unhandled Promise Rejection:\s*/, ''),
    source: 'promise',
    reason: err,
    stack: getErrorStack(err)
  }
  return isNoise(normalized)
})

appErrorService.initialize(reportErrorsToNewRelic, window.newrelic)

initIconLibrary()

const app = createApp(App)
  .component('FontAwesomeIcon', FontAwesomeIcon)
  .component('FontAwesomeLayers', FontAwesomeLayers)

// These handlers are registered before `app.use(pinia)` so that a throw during
// startup is still reported, which means `useMainStore()` can raise here — and
// now that the agent's duplicate of the error is suppressed, a raise would
// lose the error outright rather than merely stripping it of context. Report
// with fewer attributes instead.
function errorContextAttributes(): Record<string, unknown> {
  try {
    const mainStore = useMainStore()
    return {
      cadyMarket: mainStore.cadyMarket,
      cadySessionToken: mainStore.cadySessionToken
    }
  } catch {
    return {}
  }
}

app.config.errorHandler = (err, vm, info) => {
  appErrorService.reportVueError(err, vm, info, errorContextAttributes())
}

window.addEventListener('unhandledrejection', (event) => {
  appErrorService.reportUnhandledPromiseRejection(event, errorContextAttributes())
})

// `addEventListener` rather than `window.onerror`: the assignment form is a
// single slot any third-party tag on the page can overwrite, and the agent no
// longer reports a copy of what we would miss if that happened.
window.addEventListener('error', (event) => {
  appErrorService.reportWindowError(
    event.message,
    event.filename,
    event.lineno,
    event.colno,
    event.error,
    errorContextAttributes()
  )
})

const pinia = createPinia()
pinia.use(createPersistedState({ storage: createReportingStorage() }))
// Adds Vue Router instance to Pinia stores as a plugin
pinia.use(({ store }) => {
  store.router = markRaw(router)
})

app.use(pinia)

app.use(router)

app.use(Toast, {} as PluginOptions)

app.use(Vue3Mq, {
  preset: 'bootstrap5'
})

app.use(
  createGtm({
    id: import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID,
    vueRouter: router as any,
    vueRouterAdditionalEventData: () => {
      const mainStore = useMainStore()
      return mainStore.gtmCustomDimensionsMerged
    }
  })
)

app.mount('#app')
